import { useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";
import { Loader2, X, KeyRound } from "lucide-react";

export default function ResetPasswordDialog({ user, onClose, onSaved }) {
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!user) return null;

  const handleClose = () => {
    if (submitting) return;
    setPwd("");
    setError("");
    setShow(false);
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 10)
      return setError("New password must be at least 10 characters.");

    setSubmitting(true);
    try {
      await api.patch(`/admin/users/${user.id}/password`, {
        new_password: pwd,
      });
      toast.success(`Password reset for ${user.name}`, {
        description: "Share the new password with them securely.",
      });
      setSubmitting(false);
      onSaved?.();
      handleClose();
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Could not reset password."
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      onClick={handleClose}
      data-testid="reset-password-dialog"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 text-white rounded-t-2xl flex items-center justify-between"
          style={{
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
          }}
        >
          <div className="flex items-center gap-2">
            <KeyRound size={16} />
            <h3 className="text-base font-semibold">Reset password</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white"
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="reset-password-form">
          <div className="text-sm text-slate-600">
            Setting a new password for{" "}
            <span className="font-semibold text-slate-900">{user.name}</span>{" "}
            ({user.email}). Share the new password with them securely.
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              New password
            </label>
            <div className="relative">
              <input
                type={show ? "text" : "password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="At least 10 characters"
                data-testid="reset-new-input"
                className="w-full px-3 py-2 pr-16 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": BRAND.teal }}
                disabled={submitting}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1"
                tabIndex={-1}
              >
                {show ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {error && (
            <div
              className="text-sm px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700"
              data-testid="reset-error"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="reset-submit-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition disabled:opacity-70"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
              }}
            >
              {submitting && <Loader2 className="animate-spin" size={14} />}
              Reset password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
